import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Card,
  CardActionArea,
  CardContent,
  Chip,
  FormControlLabel,
  Grid,
  IconButton,
  InputAdornment,
  Switch,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import { Add as AddIcon, Edit as EditIcon, Search as SearchIcon } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { apiClient } from '../api/client';
import {
  formatActionDueDate,
  formatPatientAge,
  isActionDueDateUrgent,
  isFirstVisitIncomplete,
} from '../utils/patientDateFormat';
import type { Patient, PatientActionStatus, PatientMedicalOrganization } from '../constants/patient';
import {
  PatientMedicalInfoRequestsValue,
  PatientUnsetDateValue,
} from './patientIntake/PatientReadonlyValues';
import {
  genderLabels,
  patientActionStatusChipColor,
  patientActionStatusLabels,
  patientListStatusDisplay,
  patientStatusChipSx,
} from '../constants/patient';

/** 一覧カード内のラベル・値で行の高さ・ベースラインを揃える */
const listCardTextSx = { fontSize: '0.8125rem', lineHeight: 1.43 } as const;

const CardField: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
    <Typography
      component="span"
      variant="caption"
      color="text.secondary"
      sx={{ flexShrink: 0, width: '5rem', ...listCardTextSx }}
    >
      {label}
    </Typography>
    <Box sx={{ minWidth: 0, flex: 1, display: 'flex', alignItems: 'center' }}>{children}</Box>
  </Box>
);

const CardText: React.FC<{ value: string | null | undefined; multiline?: boolean }> = ({
  value,
  multiline = false,
}) => (
  <Typography
    component="span"
    variant="body2"
    color={value?.trim() ? 'text.primary' : 'text.secondary'}
    noWrap={!multiline}
    sx={{
      ...listCardTextSx,
      ...(multiline ? { whiteSpace: 'pre-line', wordBreak: 'break-word' } : undefined),
    }}
  >
    {value?.trim() || '—'}
  </Typography>
);

const PatientList: React.FC = () => {
  const navigate = useNavigate();
  const [patients, setPatients] = useState<Patient[]>([]);
  const [error, setError] = useState('');
  const [includeArchived, setIncludeArchived] = useState(false);
  const [search, setSearch] = useState('');
  const [referralFilter, setReferralFilter] = useState<PatientMedicalOrganization | null>(null);
  const [referralOptions, setReferralOptions] = useState<PatientMedicalOrganization[]>([]);
  const [referralOptionsLoading, setReferralOptionsLoading] = useState(false);
  const [firstVisitIncompleteOnly, setFirstVisitIncompleteOnly] = useState(false);
  const [actionStatusFilters, setActionStatusFilters] = useState<PatientActionStatus[]>([]);

  const actionStatusFilterOptions: PatientActionStatus[] = ['needs_action', 'waiting'];

  const fetchReferralOptions = useCallback(async (q: string) => {
    setReferralOptionsLoading(true);
    try {
      const res = await apiClient.get<PatientMedicalOrganization[]>('/api/patient-organizations', {
        params: { kind: 'referral', q: q || undefined },
      });
      setReferralOptions(res.data);
    } catch {
      setReferralOptions([]);
    } finally {
      setReferralOptionsLoading(false);
    }
  }, []);

  const fetchPatients = useCallback(async () => {
    try {
      const params: Record<string, boolean> = {};
      if (includeArchived) params.include_archived = true;
      const res = await apiClient.get<Patient[]>('/api/patients', { params });
      setPatients(res.data);
      setError('');
    } catch {
      setError('患者一覧の取得に失敗しました');
    }
  }, [includeArchived]);

  useEffect(() => {
    void fetchPatients();
  }, [fetchPatients]);

  useEffect(() => {
    void fetchReferralOptions('');
  }, [fetchReferralOptions]);

  const visiblePatients = useMemo(() => {
    const q = search.trim().toLowerCase();
    const filtered = patients.filter((p) => {
      if (referralFilter != null && p.referral_source?.id !== referralFilter.id) return false;
      if (firstVisitIncompleteOnly && !isFirstVisitIncomplete(p.first_visit_date)) {
        return false;
      }
      if (
        actionStatusFilters.length > 0 &&
        !actionStatusFilters.includes(p.action_status ?? 'none')
      ) {
        return false;
      }
      if (!q) return true;
      return [p.name, p.name_kana, p.patient_number]
        .filter(Boolean)
        .some((v) => String(v).toLowerCase().includes(q));
    });
    const sorted = [...filtered];
    sorted.sort((a, b) => (b.updated_at || '').localeCompare(a.updated_at || ''));
    return sorted;
  }, [patients, search, referralFilter, firstVisitIncompleteOnly, actionStatusFilters]);

  return (
    <Box sx={{ flex: 1, minHeight: 0, overflow: 'auto' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 2 }}>
        <Typography variant="h5" sx={{ flexGrow: 1, minWidth: 120 }}>
          患者一覧
        </Typography>
        <FormControlLabel
          control={
            <Switch
              checked={includeArchived}
              onChange={(_, v) => setIncludeArchived(v)}
              color="primary"
            />
          }
          label={
            <Typography component="span" variant="caption" sx={{ lineHeight: 1.6 }}>
              アーカイブを表示
            </Typography>
          }
        />
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => navigate('/patients/new')}>
          新規受付
        </Button>
      </Box>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mb: 2 }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            flexWrap: 'wrap',
          }}
        >
          <TextField
            size="small"
            placeholder="患者名・フリガナ・患者番号で検索"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            sx={{ flexGrow: 1, minWidth: 200, maxWidth: 360 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" color="action" />
                </InputAdornment>
              ),
            }}
          />
          <Autocomplete
            size="small"
            sx={{ minWidth: 200, maxWidth: 280, flexGrow: { xs: 1, sm: 0 } }}
            options={referralOptions}
            loading={referralOptionsLoading}
            value={referralFilter}
            onChange={(_, v) => setReferralFilter(v)}
            onInputChange={(_, v, reason) => {
              if (reason === 'input' || reason === 'clear') void fetchReferralOptions(v);
            }}
            getOptionLabel={(o) => o.name}
            isOptionEqualToValue={(a, b) => a.id === b.id}
            noOptionsText="該当する紹介元がありません"
            renderInput={(params) => (
              <TextField {...params} placeholder="紹介元で絞り込み" label="紹介元" />
            )}
          />
        </Box>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            flexWrap: 'wrap',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
            <Typography component="span" variant="caption" color="text.secondary" sx={{ flexShrink: 0 }}>
              対応ステータス
            </Typography>
            <ToggleButtonGroup
              size="small"
              exclusive={false}
              value={actionStatusFilters}
              onChange={(_, v) => setActionStatusFilters(v ?? [])}
              aria-label="対応ステータスで絞り込み"
            >
              {actionStatusFilterOptions.map((status) => (
                <ToggleButton key={status} value={status} aria-pressed={actionStatusFilters.includes(status)}>
                  {patientActionStatusLabels[status]}
                </ToggleButton>
              ))}
            </ToggleButtonGroup>
          </Box>
          <FormControlLabel
            control={
              <Switch
                checked={firstVisitIncompleteOnly}
                onChange={(_, v) => setFirstVisitIncompleteOnly(v)}
                color="primary"
              />
            }
            label={
              <Typography component="span" variant="caption" sx={{ lineHeight: 1.6 }}>
                初診未完了のみ
              </Typography>
            }
            sx={{ ml: 0 }}
          />
        </Box>
      </Box>

      {error ? (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      ) : null}

      {visiblePatients.length === 0 ? (
        <Box sx={{ textAlign: 'center', py: 8, color: 'text.secondary' }}>
          <Typography variant="body2" sx={{ mb: patients.length === 0 ? 2 : 0 }}>
            {patients.length === 0
              ? '登録されている患者はいません'
              : '検索条件に一致する患者がいません'}
          </Typography>
          {patients.length === 0 && (
            <Button
              variant="outlined"
              startIcon={<AddIcon />}
              onClick={() => navigate('/patients/new')}
            >
              新規受付
            </Button>
          )}
        </Box>
      ) : (
        <Grid container spacing={2}>
          {visiblePatients.map((p) => {
            const meta = patientListStatusDisplay(p);
            const birthAgeLabel = formatPatientAge(p.date_of_birth);
            const genderLabel = p.gender ? genderLabels[p.gender] || p.gender : null;
            const actionDueDateLabel = formatActionDueDate(p.action_due_date);
            const actionDueDateUrgent = isActionDueDateUrgent(p.action_due_date);
            return (
              <Grid item xs={12} sm={6} md={4} xl={3} key={p.id}>
                <Card
                  variant="outlined"
                  sx={{
                    position: 'relative',
                    height: '100%',
                    borderRadius: 2,
                    opacity: p.archived_at ? 0.72 : 1,
                    transition: 'box-shadow 0.15s',
                    '&:hover': { boxShadow: 3 },
                  }}
                >
                  <CardActionArea
                    onClick={() => navigate(`/patients/${p.id}`)}
                    sx={{
                      height: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'stretch',
                      justifyContent: 'flex-start',
                    }}
                  >
                    <CardContent
                      sx={{
                        pb: 5.5,
                        flex: 1,
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'stretch',
                      }}
                    >
                      {/* 基本情報カード同様: 患者番号↑ 氏名 フリガナ↓ + ステータスチップ */}
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'flex-start',
                          justifyContent: 'space-between',
                          gap: 1,
                          mb: 1,
                          flexShrink: 0,
                        }}
                      >
                        <Box sx={{ minWidth: 0, flex: 1 }}>
                          {p.patient_number?.trim() ? (
                            <Typography
                              variant="body2"
                              color="text.secondary"
                              noWrap
                              sx={{ fontSize: '0.8125rem', lineHeight: 1.4, mb: 0.25 }}
                            >
                              {p.patient_number}
                            </Typography>
                          ) : null}
                          <Typography
                            variant="subtitle1"
                            noWrap
                            sx={{ fontSize: '1.125rem', fontWeight: 600, lineHeight: 1.3 }}
                          >
                            {p.name?.trim() || '（名称未設定）'}
                          </Typography>
                          {p.name_kana?.trim() ? (
                            <Typography
                              variant="body2"
                              color="text.secondary"
                              noWrap
                              sx={{ mt: 0.25, fontSize: '0.8125rem' }}
                            >
                              {p.name_kana}
                            </Typography>
                          ) : null}
                        </Box>
                        <Box
                          sx={{
                            display: 'flex',
                            flexWrap: 'wrap',
                            gap: 0.5,
                            justifyContent: 'flex-end',
                            minHeight: 48,
                            alignContent: 'flex-start',
                            flexShrink: 0,
                            maxWidth: '55%',
                          }}
                        >
                          <Chip
                            label={meta.label}
                            size="small"
                            variant="outlined"
                            color={meta.chipColor}
                            sx={{ flexShrink: 0, ...patientStatusChipSx }}
                          />
                          {p.action_status && p.action_status !== 'none' ? (
                            <Chip
                              label={patientActionStatusLabels[p.action_status]}
                              size="small"
                              variant="outlined"
                              color={patientActionStatusChipColor(p.action_status)}
                              sx={{ flexShrink: 0, ...patientStatusChipSx }}
                            />
                          ) : null}
                        </Box>
                      </Box>

                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, flex: 1 }}>
                        <CardField label="性別">
                          <CardText value={genderLabel} />
                        </CardField>
                        <CardField label="生年月日">
                          <CardText value={birthAgeLabel} multiline />
                        </CardField>
                        <CardField label="住所">
                          <CardText value={p.address} multiline />
                        </CardField>
                        <CardField label="初診日">
                          <PatientUnsetDateValue iso={p.first_visit_date} compact />
                        </CardField>
                        <CardField label="同意書アポ">
                          <PatientUnsetDateValue iso={p.consent_appointment_date} compact />
                        </CardField>
                        <CardField label="診情依頼">
                          <PatientMedicalInfoRequestsValue items={p.medical_info_request} compact />
                        </CardField>
                        <Box sx={{ mt: 'auto', pt: 0.5 }}>
                          <CardField label="対応期日">
                            <Typography
                              component="span"
                              variant="body2"
                              noWrap
                              sx={{
                                ...listCardTextSx,
                                color: actionDueDateLabel
                                  ? actionDueDateUrgent
                                    ? 'error.main'
                                    : 'text.primary'
                                  : 'text.secondary',
                                fontWeight: actionDueDateUrgent ? 600 : 400,
                              }}
                            >
                              {actionDueDateLabel || '—'}
                            </Typography>
                          </CardField>
                        </Box>
                      </Box>
                    </CardContent>
                  </CardActionArea>

                  <IconButton
                    size="small"
                    aria-label="患者を編集"
                    onClick={(e) => {
                      e.stopPropagation();
                      navigate(`/patients/${p.id}/edit`);
                    }}
                    sx={{ position: 'absolute', bottom: 8, right: 8 }}
                  >
                    <EditIcon fontSize="small" />
                  </IconButton>
                </Card>
              </Grid>
            );
          })}
        </Grid>
      )}
    </Box>
  );
};

export default PatientList;
